import { Controller, ControllerConstructor } from "@hotwired/stimulus";

interface BaseControllerConstructor {
  identifier: string;
  new (...args: any[]): BaseController;
}

// Intentionally an object so that === will compare against this exact reference.
// We could use a Symbol here, but IE11 will barf and I don't think it merits a full on polyfill.
const initializeReceipt = {};

export default abstract class BaseController extends Controller {
  public static identifier: string;

  // Set the identifier as a static for the controller
  public static setIdentifier(id: string) {
    this.identifier = id;
  }

  /**
   * These lifecycle methods are provided by Stimulus.
   * Subclasses can override initialize(), connect(), and disconnect() as needed.
   * Remember to call super.initialize(), super.connect(), and super.disconnect().
   */
  public initialize() {
    const receipt = this.onInitialize();
    if (receipt !== initializeReceipt) {
      // If we get here, it means that a subclass did not call `super.onInitialize()`.
      // This guard therefore ensures that every subclass should call `super.onInitialize()`.
      throw new Error(
        `onInitialize was implemented in ${this.identifier} without a call to super.onInitialize.`,
      );
    }
  }

  // This is called by `initialize`.
  public onInitialize() {
    // This receipt is checked by `initialize`.
    // This ensures that any implementing method must call down the prototype chain,
    //  so that initializers are not skipped.
    // https://github.com/Microsoft/TypeScript/issues/21388#issuecomment-360214959
    return initializeReceipt;
  }

  public findParentElementByTagName(el: Element, tagName: string) {
    tagName = tagName.toLowerCase();

    while (el && el.parentElement) {
      el = el.parentElement;
      if (el.tagName && el.tagName.toLowerCase() === tagName) {
        return el;
      }
    }
    return null;
  }

  public get disabled() {
    return this.element.hasAttribute("disabled");
  }

  public set disabled(disabled: boolean) {
    if (disabled) {
      this.element.setAttribute("disabled", "disabled");
    } else {
      this.element.removeAttribute("disabled");
    }
  }

  // ---

  protected findController(
    element: Element,
    identifier: string,
  ): BaseController | null {
    // It would be more performant to pass the whole controller through the custom event,
    // so then it doesn't need to be looked up here every time the event is received.
    // I'm not sure how well a class sends via a DOM event, though!
    const controller = this.application.getControllerForElementAndIdentifier(
      element,
      identifier,
    );
    return controller instanceof BaseController ? controller : null;
  }

  protected toggleTargetElementClasses(
    target: Element,
    state: boolean,
    classesOff: string[],
    classesOn: string[],
  ) {
    if (state) {
      this.setTargetElementClasses(target, classesOff, classesOn);
    } else {
      this.setTargetElementClasses(target, classesOn, classesOff);
    }
  }

  protected setTargetElementClasses(
    target: Element,
    classesToRemove: string[],
    classesToAdd: string[],
  ) {
    classesToRemove.forEach((className) => target.classList.remove(className));
    classesToAdd.forEach((className) => target.classList.add(className));
  }

  protected toggleOnSearch(
    search: string,
    target: Element,
    searchTargets: HTMLElement[],
    classesOnMatch: string[],
    classesOnNoMatch: string[],
  ): boolean {
    const st = search.trim().toLowerCase();
    console.debug("toggleOnSearch, search:", {
      search,
      target,
      searchTargets,
      st,
    });
    if (st.length < 2 || st == "") {
      this.setTargetElementClasses(target, classesOnNoMatch, classesOnMatch);
      return true;
    }
    const matches =
      searchTargets.filter((searchTarget) =>
        searchTarget.innerText.toLowerCase().includes(st),
      ).length > 0;
    this.toggleTargetElementClasses(
      target,
      matches,
      classesOnNoMatch,
      classesOnMatch,
    );
    return matches;
  }

  // Emit an event with a prefix generated by asking the given controller for
  // its event prefix.
  protected emitEvent(
    target: EventTarget,
    ControllerClass: BaseControllerConstructor,
    eventName: string,
    detail: any = undefined,
    bubbles = true,
    cancelable = false,
  ) {
    console.debug(
      `[${this.identifier}] Emitting event ${ControllerClass.identifier}:${eventName}`,
    );
    this.dispatch(eventName, {
      target: target as Element,
      detail,
      prefix: ControllerClass.identifier,
      bubbles,
      cancelable,
    });
  }
}
